import React, { useEffect, useRef, useMemo } from "react";
import { useFormikContext } from "formik";
import { debounce, isEqual } from "lodash";

const usePrevious = (value: unknown) => {
  const ref = useRef<any>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

/**
 * Automatically submit after a bit of time when anything in the containing form changes.
 * Autosave works best if you do not enableReinitialize, as you can get into a situation where
 * the form gets reset when save completes which erases the user's change that happened between
 * when the save started and completed.
 * Based on https://github.com/formium/formik/blob/master/examples/DebouncedAutoSave.js
 * @param debounceMs
 * @constructor
 */
export const AutoSave: React.FC<{ debounceMs: number }> = ({ debounceMs }) => {
  const { submitForm, isSubmitting, isValid, values } = useFormikContext();
  const previousValues = usePrevious(values);

  const debouncedSubmit = useMemo(() => {
    return debounce(() => {
      return submitForm();
    }, debounceMs);
  }, [debounceMs, submitForm]);

  useEffect(() => {
    if (
      !isSubmitting &&
      isValid &&
      !isEqual(previousValues, values) &&
      previousValues !== undefined
    ) {
      // noinspection JSIgnoredPromiseFromCall
      debouncedSubmit(); //ignore, the internal state of formik is fine if we start then ignore the submit.
    }
  }, [debouncedSubmit, values, isSubmitting, isValid, previousValues]);

  return null;
};
